import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { authActions, useAuthCallback, useAuthDispatch, useAuthState } from '@packages/contexts/auth';

import {
    configService,
    authService,
    authTokenService,
    storageService,
    analyticsService,
    qmrsService,
} from '@web/services/singletons';
import { WebStorageKeys } from '@packages/core/storage';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';
import TechShareLogo from '@packages/ui/assets/images/techshare-logo.svg?react';
import subaruLogo from '@packages/ui/assets/images/subaru-logo.jpg';
import { Button, Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';
import { AccountStatusId } from '@packages/models/api';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { MainContent } from './techshareLandingPage/main-content';
import { Footer } from './techshareLandingPage/footer';
import { isDevEnvironment } from '@web/utils/is-dev-environment';
import { handleLogin } from './utils';
import constants from '@web/config/constants';
import { useContentOverlayState } from '@web/core/hooks/use-content-overlay';
import {
    usePublicationsDispatch,
    usePublicationsState,
} from '@web/vin-sessions/components/stis-publications/context/publications.hooks';
import { publicationsActions } from '@web/vin-sessions/components/stis-publications/context/publications.state';
import { ToastSuccessMessage } from '@web/vin-sessions/components/toast-success';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';

const useAuthLoadingStyles = createUseStyles({
    page: {
        backgroundColor: colors.grayOne,
        paddingTop: 120,
        height: '100vh',
        overflow: 'scroll',
        paddingBottom: 120,
    },
    landingPage: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflowY: 'hidden !important',
    },
    landingPageScrollable: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
    },
    footerWrapper: {
        position: 'sticky',
        bottom: 0,
        left: 0,
        width: '100%',
        background: colors.grayOne,
        zIndex: 2,
        '@media (max-height: 800px)': {
            position: 'static',
            zIndex: 'auto',
        },
    },
    footerWrapperNonSticky: {
        position: 'static',
        width: '100%',
        background: colors.grayOne,
        zIndex: 'auto',
    },
    card: {
        width: 450,
        marginLeft: 'auto',
        marginRight: 'auto',
        backgroundColor: colors.white,
        boxShadow: `0px 1px 4px 0px rgba(0, 0, 0, 0.16)`,
        paddingTop: 20,
        paddingLeft: 72,
        paddingRight: 72,
        paddingBottom: 72,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    subaruLogo: {
        height: 48,
        width: 180,
        display: 'block',
        margin: '0 auto',
    },
    figureContainer: {
        marginBottom: 84,
        width: 250,
    },
    textContainer: {
        textAlign: 'center',
        maxWidth: '100%',
    },
    stsLogo: {
        height: 32,
        width: 208,
        marginBottom: 48,
    },
    loginBtn: {
        marginTop: 45,
        alignSelf: 'stretch',
    },
    contentAreaOverlay: ({ showOverlay, draggable }: { showOverlay: boolean; draggable: boolean }) => ({
        zIndex: showOverlay ? 10 : -10,
        backgroundColor: showOverlay ? 'rgba(0,0,0,0.65)' : 'transparent',
        position: 'absolute',
        top: constants.headerHeight,
        bottom: 0,
        right: showOverlay ? 0 : draggable ? 0 : -390,
        left: 0,
        transition: 'right 0.5s, background-color 0.5s',
        maxWidth: '100%',
    }),
});

export const AuthLoading: FC = () => {
    const { showOverlay, dismissAllOverlays, draggable, toggledOverlays } = useContentOverlayState();
    const classes = useAuthLoadingStyles({ showOverlay, draggable });
    const { toast } = usePublicationsState();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const publicationDispatch = usePublicationsDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const authDispatch = useAuthDispatch();
    const authRouteMatch = useMatch('/auth');
    const notAuthorizedMatch = useMatch('/not-authorized');
    const query = new URLSearchParams(location.search);
    const accessToken = query.get('accessToken');
    const { account, showUnauthorizedPrompt } = useAuthState();
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const redirectParam = query.get('redirect');
    const isStisPublicRoute = location.pathname.startsWith('/stis');
    const isEmergencyGuidesRoute =
        location.pathname.startsWith('/public/emergency-guides') ||
        location.pathname.startsWith('/app/emergency-guides');
    const isContentHeavyRoute = isStisPublicRoute || isEmergencyGuidesRoute;

    const onAuthenticated = useCallback(
        (fromAccessToken: boolean) => {
            if (account) {
                analyticsService.setUser(account);
            }

            if (!fromAccessToken) {
                analyticsService.logEvent(ANALYTICS_EVENTS.USER_SUCCESSFULLY_LOGS_IN);
                googleAnalyticsService.track(GoogleAnalyticsEvents.Login);
            }

            storageService.getItem(WebStorageKeys.AuthRedirectUrl).then((authRedirect) => {
                if (account?.accountStatusId === AccountStatusId.New) {
                    authRedirect = '/onboard';
                }
                if (redirectParam) {
                    const normalized = redirectParam.startsWith('/') ? redirectParam : `/${redirectParam}`;
                    navigate(normalized, { replace: true });
                } else {
                    navigate(authRedirect || '/', { replace: true });
                }

                if (account?.accountStatusId !== AccountStatusId.New) {
                    storageService.removeItem(WebStorageKeys.AuthRedirectUrl);
                }
            });
        },
        [account, navigate, redirectParam]
    );

    const onUnauthenticated = useCallback(() => {
        if (authRouteMatch) {
            setShowLoginPrompt(!showUnauthorizedPrompt);
            navigate('/', { replace: true });
        } else if (notAuthorizedMatch) {
            return;
        } else if (location.pathname.startsWith('/stis/diag/')) {
            sessionStorage.setItem('ssm_redirect_path', location.pathname + location.search);
            handleLogin(false, navigate);
        } else {
            setShowLoginPrompt(!showUnauthorizedPrompt);
            storageService.setItem(WebStorageKeys.AuthRedirectUrl, location);
        }
    }, [authRouteMatch, navigate, location, notAuthorizedMatch, showUnauthorizedPrompt]);

    useAuthCallback({
        authTokenService,
        accessToken,
        configService,
        authService,
        storageService,
        onAuthenticated,
        onUnauthenticated,
    });

    useEffect(() => {
        storageService.getItem(WebStorageKeys.AuthRedirectUrl).then((authRedirect) => {
            if (notAuthorizedMatch && !authRedirect) {
                // somone trying to access `/not-authorized` directly
                // without going through the "sign in" prompt first
                navigate('/');
                return;
            }

            authDispatch(
                authActions.setShowUnauthorizedPrompt({
                    showUnauthorizedPrompt: !!notAuthorizedMatch && !!authRedirect,
                })
            );
            // setShowUnauthorizedPrompt(!!notAuthorizedMatch && !!authRedirect);
        });
    }, [navigate, notAuthorizedMatch]);

    useEffect(() => {
        if (toast.toastMessage.length > 0) {
            setTimeout(() => {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: '',
                            iconColor: 'white',
                            iconName: '',
                        },
                    })
                );
            }, 3000);
        }
    }, [toast]);

    useEffect(() => {
        if (authRouteMatch || (!showLoginPrompt && !showUnauthorizedPrompt)) {
            qmrsService.invalidateRetailersCache();
        }
    }, []);

    if (authRouteMatch || (!showLoginPrompt && !showUnauthorizedPrompt)) {
        return null;
    }

    return (
        <>
            {toast.toastMessage.length > 0 && (
                <ToastSuccessMessage
                    toastData={toast.toastMessage}
                    iconColor={toast.iconColor}
                    iconName={toast.iconName}
                    backgroundColor={toast.backgroundColor}
                />
            )}
            <div
                className={isContentHeavyRoute ? classes.landingPageScrollable : classes.landingPage}
                style={{
                    overflowY: toggledOverlays.escalationQuickFilters ? 'hidden' : undefined,
                    position: 'relative',
                }}
            >
                <div
                    id="right-side-panel"
                    onClick={() => {
                        dismissAllOverlays();
                    }}
                    className={classes.contentAreaOverlay}
                />
                <div style={{ flex: 1, overflowY: isContentHeavyRoute ? 'visible' : 'auto' }}>
                    <MainContent />
                </div>
                <div className={isContentHeavyRoute ? classes.footerWrapperNonSticky : classes.footerWrapper}>
                    <Footer />
                </div>
            </div>
        </>
    );
};
